/**
 * As chaves de cache do TanStack Query.
 *
 * Elas nao sao livres: os quatro primeiros prefixos sao **transcricao literal** do
 * `PERSISTED_QUERY_KEYS` do `PWA §7.3`, que decide o que pode ir para o IndexedDB. Escrever
 * `['stores']` em vez de `['public', 'stores']` faria a allowlist de persistencia deixar de
 * casar — e o modo de falha seria silencioso: a tela de lojas simplesmente pararia de
 * funcionar offline, sem erro nenhum.
 *
 * ```text
 *  PWA §7.3            ->  aqui
 *  ['public','config']     queryKeys.public.config()
 *  ['public','stores']     queryKeys.public.stores() / .store(slug)
 *  ['public','zones']      queryKeys.public.zones(storeId)
 *  ['public','categories'] queryKeys.public.categories()
 *  ['catalog','product']   queryKeys.catalog.product(slug, storeId)
 *  ['orders','list']       queryKeys.orders.list(query)
 *  ['favorites','list']    (rota prevista — nao existe no MVP)
 * ```
 *
 * Note o **singular** em `['catalog','product']`: a listagem (`['catalog','products']`, plural)
 * fica de fora da persistencia de proposito. Nao e descuido do documento — listagem carrega
 * preco de dezenas de itens, e `PWA §4.2 R11` proibe preco vencido em disco. A diferenca de uma
 * letra e normativa, e por isso esta escrita aqui.
 */

/** Uma chave de query: tupla legivel, sempre com o prefixo de familia na frente. */
export type QueryKey = readonly unknown[];

export const queryKeys = {
  public: {
    /** `GET /v1/public/config` — rota prevista; a chave existe porque a allowlist a nomeia. */
    config: () => ['public', 'config'] as const,
    stores: () => ['public', 'stores'] as const,
    store: (slug: string) => ['public', 'stores', slug] as const,
    zones: (storeId: string) => ['public', 'zones', storeId] as const,
    categories: () => ['public', 'categories'] as const,
  },

  catalog: {
    /** Listagem — **nao persistida** (preco). */
    products: (query: Readonly<Record<string, unknown>>) => ['catalog', 'products', query] as const,
    /** Ficha — persistida **apos sanitizacao** (`STRIP_FIELDS` tira preco e disponibilidade). */
    product: (slug: string, storeId: string) => ['catalog', 'product', slug, storeId] as const,
    availability: (storeId: string, skus: readonly string[]) =>
      ['catalog', 'availability', storeId, [...skus].sort()] as const,
    search: (query: Readonly<Record<string, unknown>>) => ['catalog', 'search', query] as const,
    suggest: (term: string, storeId: string) => ['catalog', 'suggest', term, storeId] as const,
  },

  cart: {
    current: () => ['cart'] as const,
  },

  orders: {
    list: (query: Readonly<Record<string, unknown>>) => ['orders', 'list', query] as const,
    detail: (orderId: string) => ['orders', 'detail', orderId] as const,
  },

  /**
   * A agenda de enderecos. **Uma chave so, sem `detail`**: `GET /v1/me/addresses` devolve a
   * lista inteira e nao ha rota de leitura por id (`API §6.2` tem duas linhas, nao tres).
   * Um `addresses.detail(id)` seria uma chave que nada preenche.
   */
  addresses: {
    list: () => ['me', 'addresses'] as const,
  },

  storePanel: {
    queue: (query: Readonly<Record<string, unknown>>) => ['store', 'queue', query] as const,
    summary: (query: Readonly<Record<string, unknown>>) => ['store', 'summary', query] as const,
  },
} as const;
